import { flatten, unflatten } from 'flat';
import get from '../../../node-helpers/lodash-style-get';

/*
 * Only supports {{ variable }} and {{ variable.subvariable }}
 * You don't need anything else
 * */

const operations: Record<string, (v: string, x: string) => boolean> = {
    includes: (v, x) => {
        if (v) {
            return v.includes(x);
        }
        return false;
    },
    includesText: (v, x) => {
        if (v) {
            const vText = Array.isArray(v) ? v.join(',') : v;
            return vText.includes(x);
        }
        return false;
    },
    equals: (v, x) => v.toString() === x.toString(),
    doesNotEqual: (v, x) => v.toString() !== x.toString(),
    truthy: v => !!v,
    falsy: v => !v,
};

const handleBarRegEx = /{{?(#[a-z]+)?[a -z]+[a-z]*}}/g;
const handleBarLogicRegEx = /{%?(#[a-z]+)?[a -z]+[a-z]*%}/g;
const shortcodeRegEx = /\[.*?\]/gim;
const opRegEx = /\(.*?\)/g;

const template = (input: string, variables: Record<string, string|number|string[]>): string => {
    let text = input;
    text = (() => {
        const matched = text.match(handleBarRegEx);

        if (!matched) {
            return text;
        }

        const found = Array.from(new Set([...matched]));

        found.forEach((original) => {
            const newString = original.slice(2).slice(0, -2).trim();
            const rep = get(variables, newString);

            if (typeof rep === 'string' || typeof rep === 'number') {
                text = text.replaceAll(original, String(rep));
            }
        });

        return text;
    })();

    text = (() => {
        const matched = text.match(shortcodeRegEx);

        if (!matched) {
            return text;
        }

        const found = Array.from(new Set([...matched]));

        found.forEach((original) => {
            const newString = original.slice(1).slice(0, -1).trim();
            const rep = get(variables, newString);

            if (typeof rep === 'string' || typeof rep === 'number') {
                text = text.replaceAll(original, String(rep));
            }
        });

        return text;
    })();

    text = (() => {
        const matched = text.match(handleBarLogicRegEx);

        if (!matched) {
            return text;
        }

        const found = Array.from(new Set([...matched]));

        found.forEach((original) => {
            const andSplits = original
                .slice(2)
                .slice(0, -2)
                .split('%and%')
                .map(x => x.trim());

            let evalResult = false;

            andSplits.forEach((operationSet) => {
                const operationSplitPoint = operationSet.lastIndexOf('.');

                if (operationSplitPoint === -1) {
                    if (evalResult) {
                        text = text.replaceAll(original, operationSet.trim()).split(' ').filter(x => !!x).join(' ');
                    } else {
                        text = text.replaceAll(original, '').split(' ').filter(x => !!x).join(' ');
                    }
                } else {
                    const varLoc = operationSet.slice(0, operationSplitPoint);
                    const op = operationSet.slice(operationSplitPoint + 1);

                    const varLocQueryReq = op.match(opRegEx);

                    if (varLocQueryReq) {
                        const varLocQuery = varLocQueryReq[0];
                        const varLocVal = varLocQuery.slice(1).slice(0, -1);
                        const opToRun = op.replace(varLocQuery, '');
                        const eqVar = get(variables, varLoc);

                        const parsedOpToRun = opToRun.trim();
                        if (parsedOpToRun && parsedOpToRun in operations && eqVar && typeof eqVar === 'string') {
                            evalResult = operations[parsedOpToRun](eqVar, varLocVal.trim());
                        }
                    }
                }
            });
        });

        return text;
    })();

    return text;
};

const transformDynamic = <T extends Record<string, unknown>>(
    inputProps: T,
    dynamicVariables: Record<string, string|number|string[]> = {},
): T => {
    const flattened = flatten({ data: inputProps }) as Record<string, unknown>;
    const newData = Object.fromEntries(Object.entries(flattened).map(([key, val]) => {
        if (typeof val === 'string') {
            return [key, template(val, dynamicVariables)];
        }
        return [key, val];
    }));
    return (unflatten(newData) as { data: T }).data;
};

export default transformDynamic;
